// Shared layout for the redaction editor and viewer.
import styled from '@emotion/styled'
import { theme } from '../../../themes/theme'
import { SecondaryButton } from '../../common/Button'
import { ColumnWithScrollbar } from '../../styles/Containers'
import { ErrorText } from '../../styles/Text'
import { ControlledInput } from '../../styles/TextField'
import {
  MUTED_TEXT_DARK,
  PLACEHOLDER_ICON,
  SECONDARY_TEXT,
  PILL_BORDER,
  THUMB_BORDER,
  PAPER,
  NOTICE_WARNING_BG,
  NOTICE_WARNING_TEXT,
  NOTICE_WARNING_ICON
} from '../../styles/colors'

export const EDITOR_RAIL_WIDTH = 360
const REDACTION_PAGE_HORIZONTAL_PADDING = '30px'

export const Shell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: 0;
  min-width: 0;
  contain: inline-size;
  overflow: hidden;
  background: ${theme.colors.background.light};
  color: ${theme.colors.text.body};
`

export const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px ${REDACTION_PAGE_HORIZONTAL_PADDING};
  background: ${theme.colors.background.white};
  border-bottom: 1px solid ${theme.colors.sectionDivider};
  flex: 0 0 auto;
`

export const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`

export const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const HeaderSubtitle = styled.p`
  font-size: 13px;
  line-height: 1.3;
  margin: 0;
  color: ${MUTED_TEXT_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
`

export const ActionTooltip = styled.span`
  display: inline-flex;
`

export const UnsavedHint = styled.span`
  font-size: 12px;
  color: ${MUTED_TEXT_DARK};
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 12px;
  background: ${theme.colors.background.light};
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px ${REDACTION_PAGE_HORIZONTAL_PADDING};
  background: ${theme.colors.background.white};
  border-bottom: 1px solid ${theme.colors.sectionDivider};
  flex: 0 0 auto;
  flex-wrap: wrap;
`

export const ToolbarLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  width: 100%;
`

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`

export const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`

export const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const ToolDivider = styled.span`
  width: 1px;
  height: 26px;
  background: ${theme.colors.sectionDivider};
  margin: 0 6px;
`

export const ToolbarSpacer = styled.span`
  flex: 1;
`

export const ToolButton = styled(SecondaryButton)`
  padding: 6px 14px;
  gap: 6px;
  border-radius: ${theme.borderRadius.button};
  background: ${props => (props.active === 'true' ? theme.colors.primaryLight : theme.colors.background.white)};
  border-color: ${props => (props.active === 'true' ? theme.colors.primary : theme.colors.secondaryButton.border)};
  color: ${props => (props.active === 'true' ? theme.colors.primary : theme.colors.text.body)};

  :hover:not([disabled]) {
    background: ${props => (props.active === 'true' ? theme.colors.primaryLight : theme.colors.secondaryButton.hover)};
    color: ${props => (props.active === 'true' ? theme.colors.primary : theme.colors.text.body)};
  }

  .MuiSvgIcon-root {
    height: 16px;
    width: 16px;
  }
`

export const SquareIconButton = styled(SecondaryButton)`
  padding: 4px;
  min-width: 30px;
  height: 30px;
  border-radius: 6px;
  background: ${props => (props.active === 'true' ? theme.colors.primaryLight : theme.colors.background.white)};
  border-color: ${props => (props.active === 'true' ? theme.colors.primary : theme.colors.secondaryButton.border)};
  color: ${props => (props.active === 'true' ? theme.colors.primary : theme.colors.text.body)};

  .MuiSvgIcon-root {
    height: 16px;
    width: 16px;
  }
`

export const ZoomLabel = styled.span`
  font-size: 14px;
  min-width: 44px;
  text-align: center;
`

export const PageInput = styled(ControlledInput)`
  width: 54px;
  height: 30px;
  text-align: center;
`

export const PageText = styled.span`
  font-size: 14px;
`

export const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`

export const CanvasScroll = styled(ColumnWithScrollbar)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: auto;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  font-size: 12px;
  color: ${MUTED_TEXT_DARK};
`

export const LegendSwatch = styled.span`
  display: inline-block;
  width: 14px;
  height: 10px;
  margin-right: 6px;
  vertical-align: middle;
  box-sizing: border-box;
  background: ${props => props.fill};
  border: ${props => (props.selected ? 2 : 1.5)}px solid ${props => props.stroke};
`

export const Rail = styled(ColumnWithScrollbar)`
  box-sizing: border-box;
  width: ${EDITOR_RAIL_WIDTH}px;
  flex: 0 0 ${EDITOR_RAIL_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px 20px 84px;
  background: ${theme.colors.background.white};
  border-left: 1px solid ${theme.colors.sectionDivider};
  overflow: auto;
`

export const RailTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`

export const RailSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`

export const RailDivider = styled.div`
  border-top: 1px solid ${theme.colors.sectionDivider};
`

export const KeyValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;

  span:first-of-type {
    color: ${MUTED_TEXT_DARK};
  }
`

export const HelperText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${MUTED_TEXT_DARK};
`

export const ReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  resize: vertical;
  box-sizing: border-box;
  padding: 10px 12px;
  font: inherit;
  font-size: 14px;
  color: ${theme.colors.text.body};
  background: ${theme.colors.inputField.background};
  border: 1px solid ${theme.colors.inputField.border};
  border-radius: ${theme.borderRadius.input};

  :focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`

export const BoxList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const BoxListItem = styled.li`
  list-style: none;
`

export const BoxListButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 0;
  font: inherit;
  font-size: 13px;
  color: ${theme.colors.text.body};
  text-align: left;
  cursor: pointer;
  background: ${props => (props.selected ? theme.colors.primaryExtraLight : 'transparent')};

  :hover {
    background: ${props => (props.selected ? theme.colors.primaryExtraLight : theme.colors.background.light)};
  }
`

export const BoxReason = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 20px;
  text-align: center;
  border-radius: 12px;
  background: ${theme.colors.background.light};
`

export const EmptyStateIcon = styled.span`
  display: inline-flex;
  color: ${PLACEHOLDER_ICON};
`

export const EmptyStateTitle = styled.strong`
  font-size: 14px;
  font-weight: 600;
`

export const FileSummaryCard = styled.div`
  margin-top: auto;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${theme.colors.background.light};
  font-size: 12px;
  line-height: 1.5;
  color: ${MUTED_TEXT_DARK};

  strong {
    display: block;
    color: ${theme.colors.text.body};
    font-size: 13px;
  }
`

export const InfoBand = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 8px ${EDITOR_RAIL_WIDTH + 30}px 0 30px;
  padding: 8px 6px 8px 14px;
  border-radius: 8px;
  background: ${theme.colors.primaryExtraLight};
  font-size: 13px;
  line-height: 1.4;
  color: ${theme.colors.text.body};
  flex: 0 0 auto;

  .MuiSvgIcon-root {
    height: 16px;
    width: 16px;
  }
`

export const EditorErrorText = styled(ErrorText)`
  padding: 6px ${REDACTION_PAGE_HORIZONTAL_PADDING} 0;
`

export const WarningBand = styled(InfoBand)`
  background: ${NOTICE_WARNING_BG};
  color: ${NOTICE_WARNING_TEXT};
  padding-right: 14px;
`

export const BandIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-self: flex-start;
  margin-top: 1px;

  .MuiSvgIcon-root {
    height: 18px;
    width: 18px;
    color: ${NOTICE_WARNING_ICON};
  }
`

export const InfoBandText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 1px;
  min-width: 0;
`

export const InfoBandDetail = styled.span`
  font-size: 12px;
  color: ${SECONDARY_TEXT};
`

export const ViewOnlyPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid ${PILL_BORDER};
  background: ${theme.colors.background.light};
  font-size: 13px;

  .MuiSvgIcon-root {
    height: 14px;
    width: 14px;
  }
`

export const InfoLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${MUTED_TEXT_DARK};

  .MuiSvgIcon-root {
    height: 15px;
    width: 15px;
  }
`

export const PageTile = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1.5px solid ${props => (props.active === 'true' ? theme.colors.primary : 'transparent')};
  background: ${props => (props.active === 'true' ? theme.colors.primaryExtraLight : 'transparent')};
  font: inherit;
  font-size: 13px;
  font-weight: ${props => (props.active === 'true' ? 600 : 400)};
  color: ${theme.colors.text.body};
  cursor: pointer;
  text-align: left;

  :hover {
    background: ${props => (props.active === 'true' ? theme.colors.primaryExtraLight : theme.colors.background.light)};
  }
`

export const PageTileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const Thumb = styled.div`
  width: 46px;
  height: 60px;
  border: 1px solid ${THUMB_BORDER};
  background: ${PAPER};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  canvas {
    max-width: 100%;
    max-height: 100%;
  }
`
